<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { useGlobalUser } from "@/stores/globalUser";
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import { useCoin } from "@/stores/coin";
import { computed, onBeforeMount, ref } from "vue";
import { CoinQuestTaskType } from "@/types/coin";
import { showLoadingToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

const user = useGlobalUser();
const coin = useCoin();
const loading = ref(false);
const router = useRouter();

onBeforeMount(async () => {
  loading.value = true;
  const promises: Array<Promise<void>> = [];
  try {
    promises.push(user.refreshUser());
    promises.push(coin.refreshQuestList());
    await Promise.all(promises);
  } finally {
    loading.value = false;
  }
});

const { t } = useI18n();
const dailyCheck = async () => {
  const loading = showLoadingToast(t("Coin.checking"));
  try {
    await coin.dailyCheck();
    await coin.refreshQuestList();
  } finally {
    loading.close();
  }
};

const currentDailyTaskProgress = computed(() => {
  return coin.sortedQuestList
    .filter((quest) => quest.coinQuest.repeatType === 1)
    .filter((quest) => quest.obtainAmount === quest.coinQuest.requireAmount)
    .reduce((acc, quest) => acc + quest.coinQuest.coinAmount, 0);
});
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto] relative story-home">
    <threads-nav :enable-switch-mate="false" show-back>
      <template #center> {{ $t("Coin.getCoin") }}</template>
    </threads-nav>
    <div v-if="loading" class="flex items-center justify-center">
      <div class="loading"></div>
    </div>
    <div v-else class="flex flex-col p-4 gap-4 overflow-y-auto">
      <div class="flex justify-between items-center bg-neutral p-3 rounded">
        <div class="flex gap-3 items-center text-2xl">
          <i class="iconfont icon-coin" />
          <span class="text-primary font-bold">{{ user.coinAmount }}</span>
        </div>
        <div
          class="flex items-center gap-2 cursor-pointer"
          @click="router.push({ name: 'coinBalanceRecord' })"
        >
          {{ $t("Coin.goGetRecordsView") }}
          <light-arrow-icon
            class="transform rotate-180 icon-sm text-base-content/50"
          />
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <div class="flex flex-col">
          <span class="text-lg">{{ $t("Coin.dailyTask") }}</span>
          <span class="text-base-content/50">
            {{ $t("Coin.dailyTaskLimit") }}:
            <span class="text-primary">{{ currentDailyTaskProgress }}</span
            >/3</span
          >
        </div>
        <div class="flex flex-col gap-2">
          <coin-task-item :coin="1" :title="$t('Coin.dailyCheck')">
            <template #icon>
              <i class="iconify mdi--calendar-check-outline" />
            </template>
            <template #right>
              <button
                v-if="!coin.hasCheckedToday"
                class="btn btn-sm btn-primary rounded"
                @click="dailyCheck"
              >
                {{ $t("Coin.dailyCheck") }}
              </button>
              <span
                v-else
                class="flex items-center justify-center text-success gap-2"
              >
                <check-outline-icon />
                {{ $t("Coin.dailyChecked") }}
              </span>
            </template>
          </coin-task-item>
          <coin-task-item
            :coin="2"
            :current-progress="
              coin.questType2QuesCountMap.get(CoinQuestTaskType.Send5Emoji) ?? 0
            "
            :max-progress="5"
            :title="$t('Coin.postReactionTask')"
          >
            <template #icon>
              <i class="iconify mingcute--thumb-up-line" />
            </template>
          </coin-task-item>
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <div class="flex flex-col">
          <span class="text-lg">{{ $t("Coin.residentTask") }}</span>
        </div>
        <coin-task-item
          :coin="10"
          :current-progress="
            coin.questType2QuesCountMap.get(CoinQuestTaskType.Get3Reply) ?? 0
          "
          :max-progress="3"
          :title="$t('Coin.reviewTask')"
          reset-if-finished
        />
      </div>
      <div class="flex flex-col gap-2">
        <div class="flex flex-col">
          <span class="text-lg">{{ $t("Coin.moreTask") }}</span>
        </div>
        <coin-task-item
          :coin="5"
          :current-progress="
            coin.questType2QuesCountMap.get(CoinQuestTaskType.SetBio) ?? 0
          "
          :max-progress="1"
          :title="$t('Coin.bioTask')"
        >
          <template #icon>
            <i class="iconify mingcute--user-edit-line" />
          </template>
        </coin-task-item>
        <coin-task-item
          :coin="5"
          :current-progress="
            coin.questType2QuesCountMap.get(CoinQuestTaskType.SetAvatar) ?? 0
          "
          :max-progress="1"
          :title="$t('Coin.updateAvatarTask')"
        >
          <template #icon>
            <i class="iconify ic--round-portrait" />
          </template>
        </coin-task-item>
      </div>
      <div class="flex flex-col gap-0.5">
        <span class="text-lg">{{ $t("Coin.coinUsageIntroTitle") }}</span>
        <div class="text-base-content/50">
          {{ $t("Coin.coinUsageIntroDesc") }}
        </div>
      </div>
      <div class="flex flex-col gap-0.5">
        <span class="text-lg">{{ $t("Coin.getCoinIntroTitle") }}</span>
        <div class="text-base-content/50">
          {{ $t("Coin.getCoinIntroDesc") }}
        </div>
      </div>
    </div>
  </div>
</template>
